/**
 * VoiceAssistantProvider — single voice controller foundation (V3-PROVIDER).
 * Mount at the authenticated app boundary when legacy hooks migrate.
 */

import React, { createContext, useContext, useMemo } from 'react';
import {
  useVoiceAssistantController,
  type UseVoiceAssistantControllerOptions,
  type VoiceAssistantControllerActions,
  type VoiceAssistantControllerState,
} from '../hooks/useVoiceAssistantController';

export type VoiceAssistantContextValue = VoiceAssistantControllerState &
  VoiceAssistantControllerActions;

const VoiceAssistantContext = createContext<VoiceAssistantContextValue | null>(null);

export type VoiceAssistantProviderProps = {
  children: React.ReactNode;
} & UseVoiceAssistantControllerOptions;

export function VoiceAssistantProvider({
  children,
  ...controllerOptions
}: VoiceAssistantProviderProps): React.ReactElement {
  const controller = useVoiceAssistantController(controllerOptions);

  const value = useMemo(
    (): VoiceAssistantContextValue => ({
      generation: controller.generation,
      uiSessionId: controller.uiSessionId,
      backendSessionId: controller.backendSessionId,
      captureId: controller.captureId,
      capturePhase: controller.capturePhase,
      activeCommandId: controller.activeCommandId,
      commandState: controller.commandState,
      pendingAction: controller.pendingAction,
      messages: controller.messages,
      isSpeaking: controller.isSpeaking,
      isConfirmSubmitting: controller.isConfirmSubmitting,
      startCapture: controller.startCapture,
      doneCapture: controller.doneCapture,
      cancelCapture: controller.cancelCapture,
      submitText: controller.submitText,
      confirmPending: controller.confirmPending,
      denyPending: controller.denyPending,
      stopSpeaking: controller.stopSpeaking,
      reset: controller.reset,
    }),
    [controller]
  );

  return (
    <VoiceAssistantContext.Provider value={value}>{children}</VoiceAssistantContext.Provider>
  );
}

export function useVoiceAssistant(): VoiceAssistantContextValue {
  const ctx = useContext(VoiceAssistantContext);
  if (!ctx) {
    throw new Error('useVoiceAssistant must be used within VoiceAssistantProvider');
  }
  return ctx;
}

/** Optional accessor for gradual migration from legacy event adapters. */
export function useOptionalVoiceAssistant(): VoiceAssistantContextValue | null {
  return useContext(VoiceAssistantContext);
}
